<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'dashboard/composables/useI18n';
import { useRoute } from 'dashboard/composables/route';
import { useEmitter } from 'dashboard/composables/emitter';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { isAConversationRoute } from 'dashboard/helper/routeHelpers';
import {
  addSafeEventListener,
  removeSafeEventListener,
} from 'dashboard/helper/domEvents';
import {
  clearSafeTimeout,
  scheduleSafeTimeout,
} from 'dashboard/helper/browserTimers';
import { safeReloadWindow } from 'dashboard/helper/browserNavigation';
import { isNavigatorOnline } from 'dashboard/helper/browserRuntime';
import {
  resolveDisconnectedState,
  resolveReconnectCompletionState,
} from 'dashboard/helper/networkNotificationState';

const { t } = useI18n();
const route = useRoute();

const RECONNECTED_BANNER_TIMEOUT = 2000;

const showNotification = ref(!isNavigatorOnline());
const isDisconnected = ref(false);
const isReconnecting = ref(false);
const isReconnected = ref(false);
let reconnectTimeout = null;

const bannerText = computed(() => {
  if (isReconnecting.value) return t('NETWORK.NOTIFICATION.RECONNECTING');
  if (isReconnected.value) return t('NETWORK.NOTIFICATION.RECONNECT_SUCCESS');
  return t('NETWORK.NOTIFICATION.OFFLINE');
});

const iconName = computed(() => (isReconnected.value ? 'wifi' : 'wifi-off'));
const canRefresh = computed(
  () => !isReconnecting.value && !isReconnected.value
);

const refreshPage = () => {
  safeReloadWindow();
};

const closeNotification = () => {
  showNotification.value = false;
  isReconnected.value = false;
  clearSafeTimeout(reconnectTimeout);
  reconnectTimeout = null;
};

const isInAnyOfTheRoutes = (routeName) => {
  return isAConversationRoute(routeName, true);
};

const applyNotificationState = (nextState) => {
  isDisconnected.value = nextState.isDisconnected;
  isReconnecting.value = nextState.isReconnecting;
  isReconnected.value = nextState.isReconnected;
  showNotification.value = nextState.showNotification;
};

const clearReconnectTimeout = () => {
  clearSafeTimeout(reconnectTimeout);
  reconnectTimeout = null;
};

const updateWebsocketStatus = () => {
  applyNotificationState(resolveDisconnectedState());
  clearReconnectTimeout();
};

const handleReconnectionCompleted = (payload = {}) => {
  const nextState = resolveReconnectCompletionState(payload);
  applyNotificationState(nextState);
  clearReconnectTimeout();

  if (!nextState.showNotification) return;

  reconnectTimeout = scheduleSafeTimeout(
    closeNotification,
    RECONNECTED_BANNER_TIMEOUT
  );
};

const handleReconnecting = () => {
  if (isInAnyOfTheRoutes(route.name)) {
    isReconnecting.value = true;
    isReconnected.value = false;
    showNotification.value = true;
  } else {
    handleReconnectionCompleted();
  }
};

const updateOnlineStatus = (event) => {
  // Case: Websocket is not disconnected
  // If the app goes offline, show the notification
  // If the app goes online, close the notification

  // Case: Websocket is disconnected
  // If the app goes offline, show the notification
  // If the app goes online but the websocket is disconnected, don't close the notification
  // If the app goes online and the websocket is not disconnected, close the notification

  if (event.type === 'offline') {
    applyNotificationState(
      resolveDisconnectedState({ isDisconnected: isDisconnected.value })
    );
    clearReconnectTimeout();
  } else if (event.type === 'online' && !isDisconnected.value) {
    handleReconnectionCompleted();
  }
};

const windowObject = typeof window !== 'undefined' ? window : null;

addSafeEventListener(windowObject, 'online', updateOnlineStatus);
addSafeEventListener(windowObject, 'offline', updateOnlineStatus);
useEmitter(BUS_EVENTS.WEBSOCKET_DISCONNECT, updateWebsocketStatus);
useEmitter(
  BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED,
  handleReconnectionCompleted
);
useEmitter(BUS_EVENTS.WEBSOCKET_RECONNECT, handleReconnecting);

onBeforeUnmount(() => {
  removeSafeEventListener(windowObject, 'online', updateOnlineStatus);
  removeSafeEventListener(windowObject, 'offline', updateOnlineStatus);
  clearReconnectTimeout();
});
</script>

<template>
  <transition name="network-notification-fade" tag="div">
    <div v-show="showNotification" class="fixed z-50 top-4 left-2 group">
      <div
        class="relative flex items-center justify-between w-full px-2 py-1 bg-yellow-200 rounded-lg shadow-lg dark:bg-yellow-700"
      >
        <fluent-icon
          :icon="iconName"
          class="text-yellow-700/50 dark:text-yellow-50"
          size="18"
        />
        <span
          class="px-2 text-xs font-medium tracking-wide text-yellow-700/70 dark:text-yellow-50"
        >
          {{ bannerText }}
        </span>
        <otello-button
          v-if="canRefresh"
          :title="$t('NETWORK.BUTTON.REFRESH')"
          variant="clear"
          size="small"
          color-scheme="warning"
          icon="arrow-clockwise"
          @click="refreshPage"
        />
        <otello-button
          variant="clear"
          size="small"
          color-scheme="warning"
          icon="dismiss"
          @click="closeNotification"
        />
      </div>
    </div>
  </transition>
</template>
